import React, { useMemo, useState } from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  alpha,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

export type DeleteConfirmationProps = {
  open: boolean;
  itemKey?: string; // e.g. "SYN-455" or filename
  onClose: () => void;

  onDelete: () => void | Promise<void>;
  onArchive?: () => void | Promise<void>;

  confirmText?: string; // default "delete"
  hideArchive?: boolean;

  isDeleting?: boolean;
  isArchiving?: boolean;

  /** Optional: customize copy */
  title?: string;
  description?: string;
};

const normalize = (value: string) => value.trim().toLowerCase();

const DeleteConfirmation = ({
  open,
  itemKey,
  onClose,
  onDelete,
  onArchive,
  confirmText = 'delete',
  hideArchive = false,
  isDeleting = false,
  isArchiving = false,
  title,
  description,
}: DeleteConfirmationProps) => {
  const [typed, setTyped] = useState('');
  const [touched, setTouched] = useState(false);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const isConfirmed = useMemo(
    () => normalize(typed) === normalize(confirmText),
    [typed, confirmText],
  );

  const showError = touched && typed.length > 0 && !isConfirmed;

  const handleExited = () => {
    setTyped('');
    setTouched(false);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && isConfirmed && !isDeleting && !isArchiving) {
      onDelete();
    }
  };

  const computedTitle = title ?? `Delete ${itemKey ?? 'this item'}?`;
  const computedDesc =
    description ??
    'This action is permanent. Deleting will remove the item and related data. Consider archiving if you might need it later.';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2.25 },
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            mt: 0.25,
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: (t) => t.palette.error.main,
            color: 'error.contrastText',
            flex: '0 0 auto',
          }}
        >
          <WarningAmberRoundedIcon fontSize="small" />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {computedTitle}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
            noWrap={!isSmDown}
          >
            Type the confirmation text to enable deletion.
          </Typography>
        </Box>

        <IconButton onClick={onClose} aria-label="Close" sx={{ mt: -0.25 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2.25}>
          {/* Danger panel */}
          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: (t) => t.palette.error.light,
              bgcolor: (t) =>
                alpha(
                  t.palette.error.main,
                  t.palette.mode === 'dark' ? 0.12 : 0.06,
                ),
              p: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Deleting is irreversible
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {computedDesc}
            </Typography>
          </Box>

          {/* Confirmation input */}
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
              Confirmation
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              Type{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontWeight: 800,
                }}
              >
                {confirmText}
              </Box>{' '}
              to continue.
            </Typography>

            <TextField
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onBlur={() => setTouched(true)}
              onKeyDown={handleKeyDown}
              placeholder={confirmText}
              size="medium"
              fullWidth
              autoFocus
              error={showError}
              helperText={
                showError
                  ? `Text does not match. Please type “${confirmText}”.`
                  : ' '
              }
              InputProps={{
                sx: {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.25 },
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          '& > button': {
            width: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{ borderRadius: 2 }}
          disabled={isDeleting || isArchiving}
        >
          Cancel
        </Button>

        {!hideArchive && onArchive && (
          <Button
            onClick={onArchive}
            disabled={isDeleting || isArchiving}
            variant="text"
            sx={{ borderRadius: 2 }}
            startIcon={isArchiving ? <CircularProgress size={16} /> : null}
          >
            Archive instead
          </Button>
        )}

        <Button
          onClick={onDelete}
          disabled={!isConfirmed || isDeleting || isArchiving}
          color="error"
          variant="contained"
          sx={{
            borderRadius: 2,
            fontWeight: 800,
            boxShadow: 'none',
            textTransform: 'none',
            py: { xs: 1.1, sm: 1 },
          }}
          startIcon={
            isDeleting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmation;
